import { State } from '@progress/kendo-data-query';
import { toODataString } from '@progress/kendo-data-query/dist/npm/odata.operators';
import { AddItemRequest } from 'src/types/addItemRequest';
import { ApplyTermsResult } from 'src/types/applyTermsResult';
import { ExcelUploadResult } from 'src/types/excelUploadResult';
import { ExcelOrderResponse } from 'src/types/excelOrderResponse';
import { getOrderItemPricingRequest } from 'src/types/getOrderItemPricingRequest';
import { PricingApprovalRequest } from 'src/types/pricingApprovalRequest';
import { LostBusinessReason } from 'src/types/lostBusinessReason';
import { ODataResponse } from 'src/types/oDataResponse';
import { OpenOrderEditModel } from 'src/types/openOrderEditModel';
import { OrderCreationResult } from 'src/types/orderCreationResult';
import { OrderEntry, SFAOrderNote } from 'src/types/orderEntry';
import { ShipTo, Term, TermType } from 'src/types/orderHeaderDropdowns';
import { Carrier } from 'src/types/Carrier';
import { CustomPricing, OrderItemPricing } from 'src/types/orderItemPricing';
import { Order, OrderListItem } from 'src/types/orderList';
import {
  CreatePerfectOrderNoteRequest,
  CreatePerfectOrderRequest,
  ItemClaimType,
  KitItem,
  PerfectOrder,
  PerfectOrderDropdownValueResult,
  PerfectOrderFileResponse,
  PerfectOrderInvoiceAddonsResponse,
  PerfectOrderView,
} from 'src/types/perfectOrder';
import { OrderSummary } from 'src/types/orderSummary';
import api from './Api';
import { POHistory } from 'src/types/poHistory';
import { TwlStatus } from 'src/types/twlStatus';
import { ApiReponse } from 'src/types/apiResponse';
import { DocumentRequest } from 'src/types/documentRequest';
import { RestrictedItem } from 'src/types/restrictedItem';
import { WarehouseStockItem } from 'src/types/warehouseStockItemResult';
import { Warehouse } from 'src/types/Warehouse';
import { ItemReturnReason } from 'src/types/itemReturnReasons';

async function getUnsubmittedOrders(dataState: State): Promise<ODataResponse<Order>> {
  return api.get<ODataResponse<Order>>(`/odata/unsubmittedOrder?${toODataString(dataState, { utcDates: false })}&$count=true`);
}

async function getOpenOrders(dataState: State, searchText: string, customerNumber: string): Promise<ODataResponse<Order>> {
  return api.get<ODataResponse<Order>>(
    `/odata/openOrder?${toODataString(dataState, { utcDates: false })}&$count=true&searchText=${encodeURIComponent(searchText)}&customerNumber=${customerNumber}`
  );
}

async function getPerfectOrders(dataState: State): Promise<ODataResponse<PerfectOrderView>> {
  return api.get<ODataResponse<PerfectOrderView>>(`/odata/PerfectOrder?${toODataString(dataState, { utcDates: false })}&$count=true`);
}

async function getPerfectOrder(perfectOrderId: number): Promise<PerfectOrder> {
  return api.get<PerfectOrder>(`/PerfectOrder/${perfectOrderId}`);
}

async function getPerfectOrderDropdownValues(): Promise<PerfectOrderDropdownValueResult[]> {
  return api.get<PerfectOrderDropdownValueResult[]>('/PerfectOrder/dropdownvalues');
}
async function getItemReturnReasons(): Promise<ItemReturnReason[]> {
  return api.get<ItemReturnReason[]>('/PerfectOrder/returnreasons');
}
async function getItemClaimTypes(): Promise<ItemClaimType[]> {
  return api.get<ItemClaimType[]>('/PerfectOrder/claimtypes');
}
async function getPerfectOrderKitItems(itemNumbers: string[], warehouse: string): Promise<KitItem[]> {
  return api.post<KitItem[]>('/PerfectOrder/kititems', { itemNumbers, warehouse });
}
async function getPerfectOrderInvoiceAddons(orderNumber: number, orderSuffix: number): Promise<PerfectOrderInvoiceAddonsResponse[]> {
  return api.get<PerfectOrderInvoiceAddonsResponse[]>(`/PerfectOrder/invoice-addons/${orderNumber}/${orderSuffix}`);
}
async function savePerfectOrder(order: PerfectOrder): Promise<void> {
  return api.put(`/PerfectOrder/${order.perfectOrderId}`, order);
}

const appendPerfectOrderFiles = (formData: FormData, files: File[]) => {
  files.forEach((file) => {
    formData.append('files', file, file.name);
  });
};

async function createPerfectOrder(order: CreatePerfectOrderRequest, files: File[]): Promise<PerfectOrder> {
  const formData = new FormData();
  const items = order.perfectOrderItems ?? [];
  const { perfectOrderItems: _perfectOrderItems, invoiceAddons: _invoiceAddons, ...orderHeader } = order;
  const mappedItems = items.map((item) => ({
    retOrderLine: item.retOrderLine,
    vendorNumber: item.vendorNumber,
    itemNumber: item.itemNumber,
    quantity: item.quantity,
    quantityUnit: item.quantityUnit,
    customerPrice: item.customerPrice,
  }));
  const invoiceAddons = order.invoiceAddons ?? [];
  const request = {
    ...orderHeader,
    PerfectOrder: orderHeader,
    Items: mappedItems,
    InvoiceAddons: invoiceAddons,
  };

  formData.append('order', JSON.stringify(request));
  appendPerfectOrderFiles(formData, files);

  // for logging
  const formDataEntries = Array.from(formData.entries()).map(([key, value]) =>
    value instanceof File ? { key, fileName: value.name, fileSize: value.size, fileType: value.type } : { key, value }
  );
  console.log('Submitting perfect order with payload:', request);
  console.log('Submitting perfect order form data:', formDataEntries);

  return api.postFile<PerfectOrder>(formData, '/PerfectOrder/upload');
}

async function uploadPerfectOrderFiles(perfectOrderId: number, files: File[]): Promise<PerfectOrderFileResponse[]> {
  const formData = new FormData();
  appendPerfectOrderFiles(formData, files);

  return api.postFile<PerfectOrderFileResponse[]>(formData, `/PerfectOrder/${perfectOrderId}/files`);
}

async function deletePerfectOrderFile(perfectOrderId: number, perfectOrderFileId: number): Promise<void> {
  return api.delete(`/PerfectOrder/${perfectOrderId}/files/${perfectOrderFileId}`);
}

async function createPerfectOrderNote(perfectOrderId: number, request: CreatePerfectOrderNoteRequest): Promise<void> {
  return api.post<void>(`/PerfectOrder/${perfectOrderId}/notes`, request);
}

async function updatePerfectOrderNote(perfectOrderId: number, perfectOrderNoteId: number, request: CreatePerfectOrderNoteRequest): Promise<void> {
  return api.put(`/PerfectOrder/${perfectOrderId}/notes/${perfectOrderNoteId}`, request);
}

async function getOpenOrderDetails(dataState: State): Promise<ODataResponse<OrderListItem>> {
  console.log(`/odata/openOrderItem?${toODataString(dataState, { utcDates: false })}&$count=true`);
  return api.get<ODataResponse<OrderListItem>>(`/odata/openOrderItem?${toODataString(dataState, { utcDates: false })}&$count=true`);
}

async function startNewWarehouseOrder(customerNumber: number, termCode: string): Promise<OrderEntry> {
  return api.get<OrderEntry>(`/OrderEntry/StartNewWarehouseOrder/${customerNumber}/termCode/${termCode}`);
}

async function startNewPlantOrder(customerNumber: number, termCode: string): Promise<OrderEntry> {
  return api.get<OrderEntry>(`/OrderEntry/StartNewPlantOrder/${customerNumber}/termCode/${termCode}`);
}

async function startNewDirectOrder(customerNumber: number, termCode: string): Promise<OrderEntry> {
  return api.get<OrderEntry>(`/OrderEntry/StartNewDirectOrder/${customerNumber}/termCode/${termCode}`);
}

async function startNewPromotionalOrder(customerNumber: number, termCode: string, programId: number): Promise<OrderEntry> {
  return api.get<OrderEntry>(`/OrderEntry/StartNewPromotionalOrder/${customerNumber}/termCode/${termCode}/program/${programId}`);
}

async function createSxOrder(sfaOrderId: number): Promise<OrderCreationResult> {
  return api.get<OrderCreationResult>(`/OrderSubmit/CreateSxOrder/SFAOrder/${sfaOrderId}`);
}

async function deleteUnsubmittedOrder(sfaOrderId: number): Promise<void> {
  return api.delete(`/OrderSubmit/Delete?sfaOrderId=${sfaOrderId}`);
}

async function copyUnsubmittedOrder(sfaOrderId: number, customerNumber: number): Promise<Order> {
  return api.get<Order>(`/OrderEntry/Copy/SFAOrder/${sfaOrderId}?customerNumber=${customerNumber}`);
}

async function getOpenOrder(orderNumber: number, orderSuffix: number): Promise<Order> {
  return api.get<Order>(`/OrderEntry/order/${orderNumber}/suffix/${orderSuffix}`);
}

async function deleteOpenOrder(orderNumber: number, orderSuffix: number, lostBusinessReason: string): Promise<void> {
  return api.get(`/OrderEntry/delete/${orderNumber}/suffix/${orderSuffix}/reason/${lostBusinessReason}`);
}

async function copyOpenOrder(orderNumber: number, orderSuffix: number, customerNumber: number): Promise<Order> {
  return api.get<Order>(`/OrderEntry/Copy/OpenOrder/${orderNumber}/Suffix/${orderSuffix}/Customer/${customerNumber}`);
}

async function editOpenOrder(orderNumber: number, orderSuffix: number): Promise<Order> {
  return api.get<Order>(`/OrderEdit/OpenOrder/${orderNumber}/Suffix/${orderSuffix}`);
}

async function getOpenOrderEditChanges(sfaOrderId: string): Promise<OpenOrderEditModel> {
  return api.get<OpenOrderEditModel>(`/OrderEdit/GetChanges/SfaOrderId/${sfaOrderId}`);
}

async function getSfaOrder(sfaOrderId: number, defaultShipTo: string | null, defaultWarehouse: string | null): Promise<OrderEntry> {
  return api.get<OrderEntry>(`/OrderEntry/V2/GetOrder?sfaOrderId=${sfaOrderId}&defaultShipTo=${defaultShipTo}&defaultWarehouse=${defaultWarehouse}`);
}

async function saveOrder(order: OrderEntry): Promise<OrderEntry> {
  console.log('OrderEntry/Save', order);
  return api.post<OrderEntry>('/OrderEntry/Save', order);
}

async function getRestrictedItems(order: OrderEntry, shipToNumber?: string, shipToState?: string): Promise<RestrictedItem[]> {
  return api.post<RestrictedItem[]>(`/OrderEntry/Restricted-Items?shipToNumber=${shipToNumber}&shipToState=${shipToState}`, order);
}

async function getRestictedItemsV2(
  itemNumbers: string[],
  shipToNumber: string,
  shipToState: string,
  customerNumber: number,
  sfaOrderid: number,
  shipToDate: string
): Promise<RestrictedItem[]> {
  return api.post<RestrictedItem[]>(
    `/OrderEntry/v2/restricted-items?shipToNumber=${shipToNumber}&shipToState=${shipToState}&customerNumber=${customerNumber}&sfaOrderid=${sfaOrderid}&shipToDate=${encodeURIComponent(
      shipToDate
    )}`,
    itemNumbers
  );
}
async function validateWarehouseCostOverride(warehouseCode: string, sfaOrderId: number): Promise<WarehouseStockItem[]> {
  return api.get<WarehouseStockItem[]>(`/OrderEntry/Cost-Overrides?warehouseCode=${warehouseCode}&sfaOrderId=${sfaOrderId}`);
}

async function saveOrderHeader(orderHeader: OrderEntry): Promise<boolean> {
  return api.post<boolean>('/OrderEntry/SaveOrderHeader', orderHeader);
}

async function addItem(request: AddItemRequest, sfaOrderId: number): Promise<OrderListItem> {
  return api.post<OrderListItem>('/OrderEntry/Item/Add/' + sfaOrderId, request);
}

async function copyItem(sfaOrderItemId: number): Promise<OrderListItem> {
  return api.post<OrderListItem>('/OrderEntry/Item/Copy/' + sfaOrderItemId, null);
}

async function updateItem(sfaOrderItemId: number, quantity: number, programId?: number): Promise<OrderListItem> {
  const url = `/OrderEntry/Item/UpdateQty/${sfaOrderItemId}?quantity=${quantity}${programId ? `&programId=${programId}` : ''}`;
  return api.post<OrderListItem>(url, null);
}

async function getOrderItemPricing(request: getOrderItemPricingRequest): Promise<OrderItemPricing> {
  return api.post<OrderItemPricing>('/OrderEntry/Item/Pricing', request);
}

async function getOrderSummary(sfaOrderId: number, customerNumber: number): Promise<OrderSummary> {
  return api.get<OrderSummary>(`/OrderEntry/Summary/Order/${sfaOrderId}/Customer/${customerNumber}`);
}

async function getConfirmationChecks(sfaOrderId: number): Promise<string[]> {
  return api.get<string[]>(`/OrderSubmit/Confirmation/${sfaOrderId}`);
}

async function saveCustomPricing(request: CustomPricing): Promise<boolean> {
  return api.post<boolean>('/OrderEntry/Item/SaveCustomPricing', request);
}

async function submitForPricingApproval(request: PricingApprovalRequest): Promise<ApiReponse> {
  return api.post<ApiReponse>('/OrderSubmit/PricingApproval', request);
}

async function resetPrice(sfaOrderItemId: number): Promise<boolean> {
  return api.get<boolean>(`/OrderEntry/Item/ResetPrice/${sfaOrderItemId}`);
}

async function applyTerms(sfaOrderId: number, termCode: string): Promise<ApplyTermsResult> {
  return api.post<ApplyTermsResult>(`/OrderEntry/ApplyTerms?sfaOrderId=${sfaOrderId}&termcode=${encodeURIComponent(termCode)}`, {});
}

async function resetAllPrices(sfaOrderId: number): Promise<boolean> {
  return api.get<boolean>(`/OrderEntry/Item/ResetAllPrices/${sfaOrderId}`);
}

async function addCostOverride(request: CustomPricing): Promise<boolean> {
  return api.post<boolean>('/OrderEntry/Item/AddCostOverride', request);
}

async function removeCostOverride(request: CustomPricing): Promise<boolean> {
  return api.post<boolean>('/OrderEntry/Item/RemoveOverride', request);
}

async function deleteItem(sfaOrderItemId: number, programId?: number): Promise<number> {
  return api.get<number>('/OrderEntry/Item/Delete/' + sfaOrderItemId + (programId ? `?programId=${programId}` : ''));
}

async function getLostBusinessReasons(): Promise<LostBusinessReason[]> {
  return api.get<LostBusinessReason[]>('/SFA/LostBusiness');
}

async function getOrderPresentation(sfaOrderId: number, sort: string, hideCustomer: boolean): Promise<any> {
  return api.get<any>(`/SFA/Presentation/${sfaOrderId}?sort=${sort}&hideCustomer=${hideCustomer}`);
}

async function getDocumentPdf(documentRequest: DocumentRequest): Promise<any> {
  return api.post<any>('/Document', documentRequest);
}

async function submitOpenOrderChanges(sfaOrderId: number): Promise<ApiReponse> {
  return api.post<ApiReponse>(`/OrderEdit/SubmitOrderChanges/SfaOrderId/${sfaOrderId}`, {});
}

async function getWarehouseTerms(defaultTermCode: string, sfaOrderId?: number) {
  return api.get<Term[]>(`/sfa/terms/warehouse/default/${defaultTermCode}/order/${sfaOrderId}`);
}

async function getVendorDirectTerms(defaultTermCode: string, vendorNumber: number) {
  return api.get<Term[]>(`/sfa/terms/vendor/${vendorNumber}/default/${defaultTermCode}`);
}

async function getTermTypes() {
  return api.get<TermType[]>('/sfa/termtypes');
}

async function getTwlStatus(orderNumber: number, orderSuffix: number): Promise<TwlStatus> {
  return api.get<TwlStatus>(`/sfa/twl/${orderNumber}/Suffix/${orderSuffix}`);
}

async function getShipTos(customerNumber: string) {
  return api.get<ShipTo[]>(`/sfa/shiptos/?customerNumber=${encodeURIComponent(customerNumber)}`);
}

async function getCarriers(customerNumber: string) {
  return api.get<Carrier[]>(`/sfa/carriers/?customerNumber=${encodeURIComponent(customerNumber)}`);
}

async function uploadOrderItems(sfaOrderId: number, formData: FormData): Promise<ExcelUploadResult> {
  return api.postFile<ExcelUploadResult>(formData, `/OrderEntry/UploadOrderItems/${sfaOrderId}`);
}

async function uploadOrder(formData: FormData): Promise<ExcelOrderResponse> {
  return api.postFile<ExcelOrderResponse>(formData, '/OrderEntry/ExcelOrder');
}

async function getPOHistory(orderNumber: string): Promise<POHistory> {
  return api.get<POHistory>(`/Item/${orderNumber}/POHistory`);
}

async function saveNotes(request: SFAOrderNote[]): Promise<SFAOrderNote[]> {
  return api.post<SFAOrderNote[]>('/Note/SaveOrderNotes', request);
}

async function createOrderFromCart(shoppingCartId: number): Promise<ApiReponse> {
  return api.post<ApiReponse>('/OrderEntry/CreateOrderFromCart', { shoppingCartId });
}

/**
 * Downloads the order items as an Excel file. Either sfaOrderId or orderNumber must be provided, both are not required.
 * @param sfaOrderId - The SFA order ID.
 * @param orderNumber - The order number.
 * @param fileName - The name of the file to download.
 * @returns A Blob representing the downloaded file.
 */
async function downloadOrderItems(sfaOrderId: number, orderNumber: string, fileName: string): Promise<Blob> {
  if (!sfaOrderId) sfaOrderId = 0;
  if (!orderNumber) orderNumber = '';
  return api.getFile(`/sfa/downloaditems/?sfaOrderId=${sfaOrderId}&orderNumber=${encodeURIComponent(orderNumber)}`, fileName);
}

/**
 * Downloads the order items with corresponding warehouse availability as an Excel file.
 * @param sfaOrderId - The SFA order ID.
 * @param warehouses - Array of filtered warehouses.
 * @returns A Blob representing the downloaded file.
 */

async function downloadItemWarehouseAvailability(sfaOrderId: number, warehouses: Warehouse[]): Promise<Blob> {
  return api.getFile(`/sfa/item-availability/?sfaOrderId=${sfaOrderId}`, 'ItemAvailability.xlsx', warehouses);
}

export default {
  getDocumentPdf,
  getLostBusinessReasons,
  getUnsubmittedOrders,
  getOpenOrder,
  getOpenOrders,
  getPerfectOrders,
  getPerfectOrder,
  getPerfectOrderDropdownValues,
  getItemClaimTypes,
  getPerfectOrderKitItems,
  getPerfectOrderInvoiceAddons,
  savePerfectOrder,
  createPerfectOrder,
  uploadPerfectOrderFiles,
  deletePerfectOrderFile,
  createPerfectOrderNote,
  updatePerfectOrderNote,
  getOpenOrderDetails,
  getOrderItemPricing,
  getOrderPresentation,
  getOrderSummary,
  getConfirmationChecks,
  startNewWarehouseOrder,
  startNewPlantOrder,
  startNewDirectOrder,
  startNewPromotionalOrder,
  getSfaOrder,
  createSxOrder,
  createOrderFromCart,
  saveOrder,
  saveOrderHeader,
  copyUnsubmittedOrder,
  deleteUnsubmittedOrder,
  deleteOpenOrder,
  copyOpenOrder,
  editOpenOrder,
  getOpenOrderEditChanges,
  addItem,
  copyItem,
  saveCustomPricing,
  submitForPricingApproval,
  resetPrice,
  resetAllPrices,
  addCostOverride,
  removeCostOverride,
  deleteItem,
  submitOpenOrderChanges,
  updateItem,
  getWarehouseTerms,
  getVendorDirectTerms,
  getTermTypes,
  getShipTos,
  getCarriers,
  applyTerms,
  uploadOrderItems,
  uploadOrder,
  getPOHistory,
  getTwlStatus,
  saveNotes,
  getRestrictedItems,
  getRestictedItemsV2,
  validateWarehouseCostOverride,
  downloadOrderItems,
  downloadItemWarehouseAvailability,
  getItemReturnReasons,
};
